import { datadogLogs } from '@datadog/browser-logs';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { useToast } from 'hephaestus/web';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { toIpsFieldConfig } from '../householdIpsComparison/householdIpsFields';
import { editedHoldings } from '../householdModelPortfolioDraft/householdAllocations';
import {
  DRAFT_ERROR_COPY,
  HouseholdModelPortfolioDraftSkeleton,
  HouseholdModelPortfolioDraftUnavailable,
} from '../householdModelPortfolioDraft/householdModelPortfolioDraftStates';
import { useHouseholdModelPortfolioDraft } from '../householdModelPortfolioDraft/useHouseholdModelPortfolioDraft';
import {
  type EditHouseholdModelPortfolioIpsOptions,
  EditHouseholdModelPortfolioIpsVisual,
  type HouseholdIpsValues,
} from './editHouseholdModelPortfolioIps.visual';

const TEST_ID = 'edit-household-model-portfolio-ips';

export type EditHouseholdModelPortfolioIpsProps = {
  options: EditHouseholdModelPortfolioIpsOptions;
  clientGroupId?: string;
  onNext: () => void;
  stepLoading?: boolean;
};

/**
 * Edits the IPS on the household's model portfolio draft. Nothing is persisted
 * to the portfolio here — the draft in workflow context is what the later steps
 * turn into a model change proposal.
 */
export const EditHouseholdModelPortfolioIps = ({
  options,
  clientGroupId,
  onNext,
  stepLoading = false,
}: EditHouseholdModelPortfolioIpsProps) => {
  const { translateBackendString } = useLocalization();
  const { setWorkflowData } = useContext(WorkflowContext);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { enqueueToast } = useToast();
  const { t } = useTranslation('workflowCompletions');
  const [saving, setSaving] = useState(false);
  const {
    draft,
    loading: loadingDraft,
    error: draftError,
    retry,
  } = useHouseholdModelPortfolioDraft(clientGroupId);

  // What the bands are totalled against. The securities step leaves its work
  // in rows beside the holdings, and those are the newer word of the two.
  const holdings = useMemo(() => editedHoldings(draft), [draft]);

  const handleContinue = async (values: HouseholdIpsValues) => {
    setSaving(true);
    try {
      // Merge against the latest context rather than the render closure, so a
      // context write that landed while this step was open is not clobbered.
      await setWorkflowData((previousData) => ({
        ...previousData,
        // The household is not chosen on this step, so an id already in context
        // is the newer word of the two and the prop only seeds one that is not
        // there yet. Writing the prop alone would clear the id whenever this
        // step is reached without it.
        currentHouseholdClientGroupId:
          previousData.currentHouseholdClientGroupId ?? clientGroupId,
        modelPortfolioDetails: {
          ...previousData.modelPortfolioDetails,
          ...values,
        },
        // Left behind so the steps that recap this IPS show the same fields
        // the advisor was given, without being configured a second time.
        householdIpsFieldConfig: toIpsFieldConfig(options),
      }));

      sendAnalytic(ovAnalyticsEvents.householdIpsCommit);
      onNext();
    } catch (caught) {
      datadogLogs.logger.error(
        'Household model portfolio IPS save failed',
        { clientGroupId },
        caught instanceof Error ? caught : undefined,
      );
      enqueueToast(t('householdModelPortfolioIps.saveError'), {
        variant: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loadingDraft || (!draft && !draftError)) {
    return (
      <HouseholdModelPortfolioDraftSkeleton
        title={translateBackendString(options?.title)}
        testId={TEST_ID}
      />
    );
  }

  if (draftError) {
    return (
      <HouseholdModelPortfolioDraftUnavailable
        title={translateBackendString(options?.title)}
        message={t(DRAFT_ERROR_COPY[draftError])}
        testId={TEST_ID}
        onRetry={draftError === 'LOAD_FAILED' ? retry : undefined}
      />
    );
  }

  return (
    <EditHouseholdModelPortfolioIpsVisual
      options={options}
      initialValues={draft}
      actuals={{
        strategy: draft?.strategy,
        securities: holdings,
        assetClassBranches: draft?.assetClassBranches,
      }}
      loading={stepLoading || saving}
      onNext={handleContinue}
    />
  );
};

export default EditHouseholdModelPortfolioIps;
